import type React from 'react';
import { t } from 'i18next';
import { ReactComponent as CheckCircleFill } from '../../../icons/check.circle.fill.svg';
import { ReactComponent as CheckCircleLine } from '../../../icons/check.circle.line.svg';
import { snakeToCamel } from '../../../utils/TextUtil.js';
import { useColorMode } from '@chakra-ui/react';

export interface CardGoodsProps {
  goodsKey: string;
  goodsName: string;
  isShowCheckIcon: boolean;
  isSelected: boolean;
  onClick: () => void;
  className?: string;
  goodsTypes?: string[];
  showGoodsTypes?: boolean;
}

const CardGoods: React.FC<CardGoodsProps> = ({
  goodsKey,
  goodsName,
  isShowCheckIcon,
  isSelected,
  onClick,
  className = '',
  goodsTypes = [],
  showGoodsTypes = false
}) => {

  const { colorMode } = useColorMode();
  // 선택된 상태의 그라데이션 오버레이 스타일
  const selectedGradientOverlayStyle = {
    position: 'absolute' as const,
    inset: 0,
    pointerEvents: 'none' as const,
    mixBlendMode: colorMode === 'dark' ? ('darken' as const) : ('color' as const),
    backgroundImage: `
      radial-gradient(circle at 0 0, rgba(255,255,255,1) 0%, rgba(255,255,255,0) 100%),
      conic-gradient(
        from 90deg,
        rgba(112, 246, 173, 1) 0%,
        rgba(97, 190, 214, 1) 15.75%,
        rgba(83, 134, 255, 1) 31.5%,
        rgba(124, 123, 255, 1) 39.875%,
        rgba(165, 113, 255, 1) 48.25%,
        rgba(206, 102, 255, 1) 56.625%,
        rgba(247, 91, 255, 1) 65%,
        rgba(214, 132, 236, 1) 73.75%,
        rgba(180, 173, 216, 1) 82.5%,
        rgba(147, 214, 197, 1) 91.25%,
        rgba(113, 255, 178, 1) 100%
      )
    `
  };

  // 선택된 상태의 그라데이션 보더 스타일
  const selectedBorderStyle = {
    backgroundImage: `
      conic-gradient(
        from 270deg at 50% 50%,
        #BEEAD2 0deg,
        #BFD2FF 113deg,
        #F5A4F9 234deg,
        #BEEAD2 360deg
      ),
      radial-gradient(
        circle at 50% 50%,
        rgba(255,255,255,0) 0%,
        rgba(255,255,255,0.25) 100%
      )
    `,
    borderRadius: '12px',
    boxShadow: '0 5px 8px -4px rgba(125,0,146,0.10)',
    padding: '3px'
  };

  // 특전 타입 배지 스타일
  const goodsTypeBadgeStyle = {
    background: 'linear-gradient(90.47deg, #FF45A9 0%, #FF6A6A 100%)',
    color: 'white',
    fontWeight: '600'
  };

  const joinGoodsTypes = () => {
    if (goodsTypes.length === 0) {
      return t('GoodsGiftBadge.base');
    }

    return goodsTypes.map((goods) => t(`GoodsGiftBadge.${snakeToCamel(goods)}`)).join('・');
  };

  return (
    <div
      className={`relative rounded-xl w-full h-full ${className}`}
      style={isSelected ? selectedBorderStyle : { padding: '3px' }}
      onClick={onClick}
    >
      {/* 체크 아이콘 - 선택 상태에 따라 다른 아이콘 표시 */}
      {isShowCheckIcon && (
        <div className="absolute top-[4px] right-[4px] w-[24px] h-[24px] z-20 flex items-center justify-center">
          {isSelected ? (
            <CheckCircleFill
              width={18}
              height={18}
              className="fill-primary-light dark:fill-primary-dark"
            />
          ) : (
            <CheckCircleLine width={18} height={18} className="fill-text-sub-lv2" />
          )}
        </div>
      )}

      {/* 특전 타입 표시 */}
      {showGoodsTypes && (
        <div className="absolute top-[3px] left-[3px] z-20">
          <div
            className={`text-[10px] px-[8px] py-[3px] rounded-tl-[10px] rounded-br-[10px] ${
              goodsTypes.length > 0 ? '' : ' bg-none text-text-sub-lv1 py-[8px]'
            }`}
            style={goodsTypes.length > 0 ? goodsTypeBadgeStyle : undefined}
          >
            {joinGoodsTypes()}
          </div>
        </div>
      )}

      {/* 메인 버튼 */}
      <div
        className={`relative z-10 flex flex-col items-center justify-center px-[16px] py-[18px] rounded-[10px] w-full h-full
          ${isSelected ? 'bg-background-light dark:bg-background-dark' : 'bg-surface-lv1 dark:bg-surface-dark'}`}
      >
        {/* 아이콘 영역 */}
        <div className="relative w-[60px] h-[60px] rounded-xl overflow-hidden">
          <img
            src={`/assets/icons/goods/Goods.${goodsKey}.png`}
            alt={`Goods.${goodsKey}`}
            className="w-[60px] h-[60px] object-contain"
          />
          {/* 선택된 상태일 때만 그라데이션 오버레이 적용 */}
          {isSelected && <div style={selectedGradientOverlayStyle} className="rounded-xl" />}
        </div>

        {/* 제목 */}
        <div className="flex items-center justify-center text-center font-medium text-[13px] leading-[16px] h-[32px] text-main-light-2 dark:text-main-dark-2">
          {goodsName}
        </div>
      </div>
    </div>
  );
};

export default CardGoods;
